import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  TableCell,
  useTheme,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useStyles } from '../ExaminationTable.style';
import { useDispatch, useSelector } from 'react-redux';
import DeleteIcon from '@material-ui/icons/Delete';
import SyncIcon from '@material-ui/icons/Sync';
import PersonAddDisabled from '@material-ui/icons/PersonAddDisabled';
import LayersClearIcon from '@material-ui/icons/LayersClear';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../../../../store/index';
import { fetchGet, fetchPost, fetchPut } from '../../../../../../helpers/fetching';
import {
  setActionToConfirm,
  setSnackbar,
  deleteExams,
  deletePatient,
  fetchPriorExaminations,
  movePriorExaminations,
} from '../../../../../../store';
import YoutubeSearchedForIcon from '@material-ui/icons/YoutubeSearchedFor';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { fetchAndMovePriorExaminations } from '../../../../../../store/slices/examinations';

function ExaminationTableActions({
  study_id,
  patient_id,
  isNestedExam,
  nestedRowTitle,
  singleExam,
  prior,
  isRemoved,
  priorList,
  handleRowClick,
  columnWidth,
}) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const currentRole = localStorage.getItem('role');

  const managementAccess = currentRole === 'admin' || currentRole === 'supervisor';

  const { currentlyProcessed, currentExam, fetchingExamList } = useSelector((state) => state.examinations);
  const { exam_list } = useSelector((state) => state.examinations);
  const { isReady } = useSelector((store) => store.global_settings);

  const patient_name = exam_list.find((exam) => exam.patient_id === patient_id)?.name;

  const selected = currentExam === study_id;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDeleteExam = (current_id, patient_name, removeFromDB = false) => {
    dispatch(
      setActionToConfirm({
        action: () => dispatch(deleteExams([current_id], t, removeFromDB)),
        text: removeFromDB
          ? t('Confirm.delete_exam_confirm', { study_name: patient_name })
          : t('Confirm.remove_images_study_confirm', { study_name: patient_name }),
      }),
    );
    handleClose();
  };

  const handleDeletePatient = (patient_id, patient_name, removeFromDB = false) => {
    dispatch(
      setActionToConfirm({
        action: () => dispatch(deletePatient(patient_id, t, removeFromDB)),
        text: removeFromDB
          ? t('Confirm.patient_delete_confirm', { patient_name: patient_name })
          : t('Confirm.remove_images_patient_confirm', { patient_name: patient_name }),
      }),
    );
    handleClose();
  };

  const handleRefreshExam = async (current_id) => {
    const studyIDs = priorList && priorList.length > 0 ? priorList : [current_id];

    const response = await fetchPut('reprocess', { exam_ids: studyIDs }, 'bbox');

    if (response.success) {
      setTimeout(() => {
        dispatch(
          actions.setSnackbar({
            msg: 'Table.reprocessing_active',
            severity: 'success',
          }),
        );
      }, 3000);
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
    }
    handleClose();
  };

  const handleSearchPriors = (current_id) => {
    dispatch(
      setSnackbar({
        msg: 'Settings.searching_priors',
        severity: 'info',
      }),
    );
    handleRowClick(current_id);
    dispatch(fetchAndMovePriorExaminations([patient_id], current_id, t));
    handleClose();
  };

  const handlePullFromPACS = (study_id) => {
    dispatch(movePriorExaminations([study_id], t));
    dispatch(
      setSnackbar({
        msg: 'ResultView.pulling_from_pacs',
        severity: 'info',
      }),
    );
  };

  const getDeleteItems = () => {
    const items = [];
    if (isNestedExam) {
      if (!isRemoved) {
        items.push(
          <MenuItem
            onClick={() => handleDeleteExam(study_id, patient_name, false)}
            className={classes.table_actions_menu}
            id='examination-actions-delete'
            key='examination-actions-delete-false'>
            <LayersClearIcon fontSize='small' className={classes.menu_icon} />
            <Typography className={classes.menu_text}> {t('Confirm.remove_images_study')} </Typography>
          </MenuItem>,
        );
      }
      if (managementAccess) {
        items.push(
          <MenuItem
            onClick={() => handleDeleteExam(study_id, patient_name, true)}
            className={classes.table_actions_menu}
            id='examination-actions-delete'
            key='examination-actions-delete-true'>
            <DeleteIcon fontSize='small' className={classes.menu_icon} />
            <Typography className={classes.menu_text}> {t('Confirm.delete')} </Typography>
          </MenuItem>,
        );
      }
    } else {
      if (!isRemoved) {
        items.push(
          <MenuItem
            onClick={() => handleDeletePatient(patient_id, patient_name, false)}
            className={classes.table_actions_menu}
            id='examination-actions-delete-patient'
            key='handle-patient-delete-false'>
            <LayersClearIcon fontSize='small' className={classes.menu_icon} />
            <Typography className={classes.menu_text}>{t('Confirm.remove_images_patient')}</Typography>
          </MenuItem>,
        );
      }
      if (managementAccess) {
        items.push(
          <MenuItem
            onClick={() => handleDeletePatient(patient_id, patient_name, true)}
            className={classes.table_actions_menu}
            id='examination-actions-delete-patient'
            key='handle-patient-delete-true'>
            <PersonAddDisabled fontSize='small' className={classes.menu_icon} />
            <Typography className={classes.menu_text}>{t('Confirm.patient_delete')}</Typography>
          </MenuItem>,
        );
      }
    }

    return items;
  };

  return (
    <TableCell
      className={classes.tr_icon_padding}
      align='center'
      key='actions'
      style={{
        paddingRight: '2px',
        ...(columnWidth
          ? {
              width: columnWidth,
              minWidth: columnWidth,
              maxWidth: columnWidth,
              boxSizing: 'border-box',
            }
          : {}),
      }}>
      {isReady ? (
        study_id === currentlyProcessed ? (
          <IconButton size='small'>
            <CircularProgress
              style={{
                maxWidth: '16px',
                height: '16px',
                marginRight: '2px',
              }}
              color='secondary'
            />
          </IconButton>
        ) : (
          <>
            {fetchingExamList || currentlyProcessed !== null ? (
              <IconButton style={{ color: '#a1a1a1' }} size='small' disabled>
                <MoreVertIcon fontSize='small' />
              </IconButton>
            ) : (
              <>
                <IconButton
                  key='examination-table-actions'
                  style={{ color: theme.palette.accents.main }}
                  size='small'
                  onClick={handleMenuClick}
                  id='examination-table-actions'>
                  <MoreVertIcon fontSize='small' />
                </IconButton>
                {anchorEl && (
                  <Menu
                    id={`menu-popular-card-${study_id}`}
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    variant='selectedMenu'
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}>
                    {/* {((nestedRowTitle && !isNestedExam) || singleExam) && (
                    <MenuItem
                      key='examination-actions-fetch-prior'
                      onClick={() => {
                        handleSearchPriors(study_id);
                      }}
                      className={classes.table_actions_menu}
                      id='examination-actions-fetch-prior'>
                      <YoutubeSearchedForIcon fontSize='small' className={classes.menu_icon} />
                      <Typography className={classes.menu_text}>{t('Confirm.fetch_priors')}</Typography>
                    </MenuItem>
                  )} */}
                    {
                      <MenuItem
                        key='examination-actions-refresh'
                        onClick={() => handleRefreshExam(study_id)}
                        className={classes.table_actions_menu}
                        id='examination-actions-refresh'>
                        <SyncIcon fontSize='small' className={classes.menu_icon} />
                        <Typography className={classes.menu_text}>
                          {nestedRowTitle ? t('Confirm.refresh_all') : t('Confirm.refresh')}
                        </Typography>
                      </MenuItem>
                    }

                    {!!isRemoved && (
                      <MenuItem
                        key='examination-actions-delete'
                        onClick={() => handlePullFromPACS(study_id)}
                        className={classes.table_actions_menu}
                        id='examination-actions-delete'>
                        <CloudDownloadIcon fontSize='small' className={classes.menu_icon} />
                        <Typography className={classes.menu_text}> {t('Confirm.restore_images')} </Typography>
                      </MenuItem>
                    )}

                    {getDeleteItems()}
                  </Menu>
                )}
              </>
            )}
          </>
        )
      ) : (
        <div />
      )}
    </TableCell>
  );
}

export default ExaminationTableActions;
